import { Card } from '@magnetic/card'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Tag } from '@magnetic/tag'
import { Button } from '@magnetic/button'
import { Spinner } from '@magnetic/spinner'
import { Copy } from '@phosphor-icons/react'
import { ArrowSquareOut, ArrowsClockwise } from '@/lib/icons'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'
import { upsertApplicationGitHubVariables } from '@/lib/api'
import type { Application as ProtoApplication, Project as ProtoProject } from '@/lib/generated-types'
import type { UpsertApplicationGitHubVariablesResponse } from '@/gen/catpocs/v1/applications_pb'

/** AWS/ECR guidance: role and registry are project-scoped; ECR repository name is per-application. */
interface AwsGitHubActionsCardProps {
  project?: Pick<ProtoProject, 'awsRoleArn' | 'ecrRegistry'> | null
  application: Pick<ProtoApplication, 'id' | 'name' | 'ecrRegistry' | 'ecrRepositoryName'>
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(
    () => {
      // Could use a toast here if available
    },
    () => {}
  )
}

const DEFAULT_AWS_REGION = 'us-east-2'

function toEnvSuffix(raw: string): string {
  const normalized = raw
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  if (!normalized) {
    return 'APP'
  }
  if (/^[0-9]/.test(normalized)) {
    return `APP_${normalized}`
  }
  return normalized
}

export function AwsGitHubActionsCard({ project, application }: AwsGitHubActionsCardProps) {
  const syncVariablesMutation = useMutationWithToast<UpsertApplicationGitHubVariablesResponse>(upsertApplicationGitHubVariables, {
    successTitle: 'GitHub variables updated',
    successMessage: (data) => `Repo ${data.repository}: ${data.created.length} created, ${data.updated.length} updated, ${data.unchanged.length} unchanged.`,
    errorTitle: 'Failed to update GitHub variables',
    errorMessage: (error) => error.message,
  })

  const awsRoleArn = project?.awsRoleArn ?? ''
  const ecrRegistry = project?.ecrRegistry ?? application?.ecrRegistry ?? ''
  const ecrRepositoryName = application?.ecrRepositoryName ?? ''
  if (!awsRoleArn) return null

  const fullEcrImage = ecrRegistry && ecrRepositoryName
    ? `${ecrRegistry}/${ecrRepositoryName}`
    : ''
  const awsRegion = DEFAULT_AWS_REGION
  const appKeySource = application?.name || ecrRepositoryName.split('/').pop() || 'app'
  const appSuffix = toEnvSuffix(appKeySource)
  const appRepoVarName = `ECR_REPOSITORY_${appSuffix}`
  const appImageVarName = `ECR_IMAGE_${appSuffix}`
  const canSyncVariables = !!application.id

  const repoVars: { name: string; value: string; scope: 'shared' | 'app' }[] = [
    { name: 'AWS_ROLE_ARN', value: awsRoleArn, scope: 'shared' },
    { name: 'AWS_REGION', value: awsRegion, scope: 'shared' },
    ...(ecrRegistry ? [{ name: 'ECR_REGISTRY', value: ecrRegistry, scope: 'shared' as const }] : []),
    ...(ecrRepositoryName ? [{ name: appRepoVarName, value: ecrRepositoryName, scope: 'app' as const }] : []),
    ...(fullEcrImage ? [{ name: appImageVarName, value: fullEcrImage, scope: 'app' as const }] : []),
  ]

  return (
    <Card style={{ flex: '1 1 100%', boxShadow: 'none' }}>
      <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
        <Heading size="sub-section">Push to ECR (GitHub Actions)</Heading>
        <Text size="p4" color="light">
          Use the IAM role and ECR repository below in your GitHub Actions workflow to push container images without long-lived credentials.
        </Text>

        <Flex direction="vertical" gap="sm" style={{ marginTop: 8 }}>
          <Text size="p3" weight="semi-bold">GitHub repository variables</Text>
          <Text size="p4" color="light">
            In your repo go to <strong>Settings → Secrets and variables → Actions</strong>, open the <strong>Variables</strong> tab, and create these repository variables.
            Shared variables can be reused by all apps in the project; app-specific variables are suffixed to avoid collisions when one GitHub repo builds multiple applications.
          </Text>
          <Text size="p4">
            <strong>Important:</strong> update your GitHub Actions workflow and replace the existing <code>env</code> definitions with the block below.
            If your workflow currently uses a generic variable like <code>ECR_REPOSITORY</code>, replace it with the app-specific variable
            <code>{` ${appRepoVarName} `}</code> to target the correct repository.
          </Text>
          <Flex align="center" gap="sm" wrap>
            <Button
              kind="primary"
              onClick={() => syncVariablesMutation.mutate({ id: application.id })}
              disabled={!canSyncVariables || syncVariablesMutation.isPending}
              icon={
                syncVariablesMutation.isPending
                  ? <Spinner size="sm" aria-label="Updating GitHub variables" />
                  : <ArrowsClockwise size={14} />
              }
            >
              {syncVariablesMutation.isPending ? 'Updating Variables...' : 'Create/Update Variables in GitHub'}
            </Button>
            <Text size="p4" color="light">
              Requires GitHub App repository permissions for Actions variables (read/write).
            </Text>
          </Flex>
          <Flex direction="vertical" gap="xs" style={{ marginTop: 4 }}>
            {repoVars.map(({ name, value, scope }) => (
              <Flex key={name} align="center" gap="sm" wrap>
                <Tag>{name}</Tag>
                <Text size="p4" color="light" style={{ minWidth: 48 }}>
                  {scope === 'shared' ? 'shared' : 'app'}
                </Text>
                <Text size="p4" style={{ wordBreak: 'break-all', flex: 1 }}>{value}</Text>
                <Button
                  size="sm"
                  kind="secondary"
                  icon={<Copy size={14} />}
                  onClick={() => copyToClipboard(value)}
                  title={`Copy value for ${name}`}
                />
              </Flex>
            ))}
          </Flex>
          <Text size="p4" color="light" style={{ marginTop: 8 }}>
            Example <code>env</code> block for your workflow (matches the variables above):
          </Text>
          <pre
            style={{
              margin: 0,
              padding: 12,
              background: 'var(--mds-color-theme-background-secondary-normal, #f5f5f5)',
              borderRadius: 8,
              fontSize: 12,
              overflow: 'auto',
            }}
          >
{`env:
  AWS_ROLE_ARN: \${{ vars.AWS_ROLE_ARN }}
  AWS_REGION: \${{ vars.AWS_REGION }}
  ECR_REGISTRY: \${{ vars.ECR_REGISTRY }}
  ECR_REPOSITORY: \${{ vars.${appRepoVarName} }}`}
          </pre>
          <Button
            size="sm"
            kind="secondary"
            icon={<ArrowSquareOut size={14} />}
            onClick={() => window.open('https://docs.github.com/en/actions/deployment/security-hardening-your-deployments/configuring-openid-connect-in-amazon-web-services', '_blank')}
          >
            GitHub OIDC with AWS docs
          </Button>
        </Flex>
      </Flex>
    </Card>
  )
}
